var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ENVIRONMENT_IS_WEB } from "../../messages/environment";
import { busService, busServiceMain, DedicatedWorker, WorkerStartedMessage, ConnectionRefusedMessage, SystemInitMessage, HandshakeModuleMessage, ResponseHandshakeModuleMessage } from "../bus";
import { generateUUID, ModuleId } from "flavr-common";
import { BASE_URL } from "../html";
import { ModelModule } from "@modules/model/ModelModule";
if (!ENVIRONMENT_IS_WEB) {
    throw new Error("worker factory can only be used in main!");
}
// Factory to spawn WebWorkers and connect them with the message bus (which is a SharedWebWorker).
class WorkerFactory {
    constructor() {
        // all the created workers are stored here, by their unique workerId
        this.workers = new Map();
        this.modules = new Map();
        // when all workers have created we can begin comparing workers.length with workersStarted
        this.allWorkersCreated = false;
        // workers should signal (with WorkerStartedMessage) when they received the bus, so we can start with SystemInitMessage when all are ready
        this.workersStarted = new Set();
        this.busConnection = busServiceMain.connection("workerFactory");
        this.moduleRegistry = new Set();
        this.workerRegistry = new Set();
        this.afterInitCallback = () => { };
        this.dedicatedWorkers = new Map();
        this.busConnection.subscribeByType(ConnectionRefusedMessage, (mgs) => {
            console.error(`[app] Busworker refused connection: ${mgs.reason}`);
            location.reload();
        });
    }
    getWorker(name) {
        return this.dedicatedWorkers.get(name);
    }
    isReady() {
        return this.allWorkersCreated && this.workersStarted.size === this.workers.size;
    }
    registerWorker(name, url) {
        this.workerRegistry.add({
            name,
            url: url !== null && url !== void 0 ? url : `${BASE_URL}/${name}.bundle.js`
        });
    }
    registerDedicatedWorker(name, worker) {
        if (this.dedicatedWorkers.has(name)) {
            throw new Error("Dedicated worker " + name + " already exists!");
        }
        try {
            this.dedicatedWorkers.set(name, new DedicatedWorker(name, worker));
        }
        catch (error) {
            console.error("[app]", error);
        }
    }
    clearWorkerRegistry() {
        this.workerRegistry.clear();
    }
    registerModule(type) {
        this.moduleRegistry.add(type);
    }
    getModule(type) {
        return this.modules.get(type);
    }
    clearModuleRegistry() {
        this.moduleRegistry.clear();
    }
    spawnWorker(name, url, appUid) {
        if (this.workers.has(name)) {
            throw new Error("Worker " + name + " already exists!");
        }
        // start new worker
        const worker = this.createWorkerFallback(name, url);
        this.workers.set(name, worker);
        // give bus port to worker
        void busServiceMain.registerWorker(worker, appUid);
        return worker;
    }
    createWorkerFallback(workerId, workerUrl) {
        let worker = null;
        // eslint-disable-next-line no-useless-catch
        try {
            let blob;
            try {
                blob = new Blob([`self.workerId = '${workerId}';importScripts('${workerUrl}');`], {
                    type: "application/javascript"
                });
            }
            catch (e) {
                // @ts-expect-error creating the Blob can fail
                const blobBuilder = new (window.BlobBuilder || window.WebKitBlobBuilder || window.MozBlobBuilder)();
                blobBuilder.append(`self.workerId = '${workerId}';importScripts('${workerUrl}');`);
                blob = blobBuilder.getBlob("application/javascript");
            }
            const url = window.URL || window.webkitURL;
            const blobUrl = url.createObjectURL(blob);
            worker = new Worker(blobUrl, {
                name: workerId
            });
            return worker;
        }
        catch (e1) {
            throw e1;
            // if it still fails, there is nothing much we can do
        }
    }
    maybeSendSystemInitMessage() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.allWorkersCreated && this.workersStarted.size === this.workers.size) {
                const waitForModuleHandshake = (moduleId, resendInterval) => __awaiter(this, void 0, void 0, function* () {
                    while (true) {
                        let timeoutHandler;
                        try {
                            yield new Promise((resolve, reject) => {
                                const askMsg = busService.createAskMessage(new HandshakeModuleMessage(moduleId), (answerMsgs) => {
                                    if (answerMsgs.length !== 1) {
                                        reject(answerMsgs);
                                        return;
                                    }
                                    const msg = answerMsgs[0];
                                    if (!ResponseHandshakeModuleMessage.is(msg)) {
                                        reject(answerMsgs);
                                        return;
                                    }
                                    resolve(msg);
                                });
                                this.busConnection.send(askMsg);
                                timeoutHandler = self.setTimeout(() => {
                                    timeoutHandler = undefined;
                                    if (askMsg) {
                                        busService.popWorkerIdForAskId(askMsg.askId);
                                    }
                                    reject(`waiting for module "${moduleId}" handshake took to long!`);
                                }, resendInterval);
                            });
                            return;
                        }
                        catch (error) {
                            console.warn(`[app] Couldn't send system init message`, error);
                        }
                        finally {
                            if (timeoutHandler !== undefined) {
                                self.clearTimeout(timeoutHandler);
                            }
                        }
                    }
                });
                yield waitForModuleHandshake(ModuleId.ROOM_MANAGER, 5000);
                this.busConnection.send(new SystemInitMessage(window.location.href));
                this.afterInitCallback();
            }
        });
    }
    init(afterInitCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            if (afterInitCallback) {
                this.afterInitCallback = afterInitCallback;
            }
            // generate a uuid for this instance of the FlaVR App,
            // so we can distinguish instance of the FlaVR App,
            // which are opened in different browser tabs.
            const appUid = generateUUID();
            // ensure there is a shared worker instance.
            yield busServiceMain.createSharedWorker(appUid);
            for (const dWorker of this.dedicatedWorkers.values()) {
                busServiceMain.registerDedicatedWorker(dWorker, appUid);
                console.debug(`[app] Created dedicated worker "${dWorker.workerId}" (${appUid})`);
            }
            const modelModule = new ModelModule();
            this.modules.set(ModelModule, modelModule);
            for (const worker of this.workerRegistry) {
                this.create(worker.name, worker.url, appUid);
                console.debug(`[app] Created worker "${worker.name}" (${appUid})`);
            }
            this.clearWorkerRegistry();
            for (const ModuleConstructor of this.moduleRegistry) {
                const module = new ModuleConstructor();
                this.modules.set(ModuleConstructor, module);
                console.debug(`[app] Created module "${module.getName()}"`);
            }
            this.clearModuleRegistry();
            this.allWorkersCreated = true;
        });
    }
    create(name, url, appUid) {
        const worker = this.spawnWorker(name, url, appUid);
        const func = (e) => {
            const msg = e.data;
            if (WorkerStartedMessage.is(msg)) {
                worker.removeEventListener("message", func);
                this.workersStarted.add(msg.workerId);
                void this.maybeSendSystemInitMessage();
            }
        };
        worker.addEventListener("message", func);
        worker.addEventListener("error", (e) => {
            console.error("[app]", name + " Worker died! recreating...", e);
            // tell the bus to clean up the worker port
            busServiceMain.workerDied(name);
            if (onerror) {
                onerror(e);
            }
            // remove old worker;
            worker.terminate();
            this.workers.delete(name);
            this.workersStarted.delete(name);
            // recreate it
            this.spawnWorker(name, url, appUid);
        });
        return worker;
    }
}
export const workerFactory = new WorkerFactory();
